/** Calls the ai-chat edge function and collects the streamed answer into one string. */
import { supabase } from "@/integrations/supabase/client";

const CHAT_URL = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/ai-chat`;

export async function askAi(messages: { role: "user" | "assistant"; content: string }[]): Promise<string> {
  const { data: { session } } = await supabase.auth.getSession();
  if (!session?.access_token) throw new Error("Please sign in to use the assistant.");

  const resp = await fetch(CHAT_URL, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      apikey: import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY,
      Authorization: `Bearer ${session.access_token}`,
    },
    body: JSON.stringify({ messages }),
  });

  if (resp.status === 429) throw new Error("Too many AI requests — try again shortly.");
  if (resp.status === 402) throw new Error("AI credits are exhausted for this workspace.");
  if (!resp.ok || !resp.body) throw new Error("The assistant is unavailable right now.");

  const reader = resp.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";
  let text = "";
  for (;;) {
    const { done, value } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    let idx: number;
    while ((idx = buffer.indexOf("\n")) !== -1) {
      let line = buffer.slice(0, idx);
      buffer = buffer.slice(idx + 1);
      if (line.endsWith("\r")) line = line.slice(0, -1);
      if (!line.startsWith("data: ")) continue;
      const json = line.slice(6).trim();
      if (json === "[DONE]") continue;
      try {
        const chunk = JSON.parse(json)?.choices?.[0]?.delta?.content as string | undefined;
        if (chunk) text += chunk;
      } catch { /* partial chunk */ }
    }
  }
  return text.trim();
}
